import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { areaRiskSeries } from '../../../../../shared/riskregister/executiveCharts';
import type { Payload, Row, Tier } from '../../../../../shared/riskregister/types';
import '../../executiveCharts.css';

const TIER_COLORS: Record<Tier, string> = {
  critical: 'var(--accent-red)',
  high: 'var(--accent-amber)',
  moderate: 'var(--accent-blue)',
  unscored: 'var(--text-muted)',
};

const VISIBLE_AREAS = 8;

export const AreaRiskChart: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
}> = ({ payload, rows }) => {
  const areas = React.useMemo(() => areaRiskSeries(rows, payload), [payload, rows]);

  if (!areas.length) {
    return <p className="risk-register__empty">No open risk is recorded for any platform area in this selection.</p>;
  }

  // Recharts draws the first row of a vertical bar chart at the bottom, so reverse for highest at the top.
  const visible = areas.slice(0, VISIBLE_AREAS);
  const chartData = visible.slice().reverse();
  const hidden = areas.slice(VISIBLE_AREAS);
  const hiddenTotal = hidden.reduce((sum, area) => sum + area.total, 0);
  const description = visible
    .map(
      (area) =>
        `${area.area}: ${area.total} open, ${area.overdue} late, ${payload.tier_order
          .map((tier) => `${payload.tiers[tier].short} ${area[tier]}`)
          .join(', ')}`,
    )
    .join('. ');

  return (
    <div className="risk-register__chart-panel">
      <p className="risk-register__chart-verdict">
        <span className="risk-register__chart-figures">
          {visible[0].area} carries the most open risk: {visible[0].total} open item
          {visible[0].total === 1 ? '' : 's'}, {visible[0].critical} critical and {visible[0].high} high,{' '}
          {visible[0].overdue} of them late.
        </span>
      </p>

      <div className="risk-register__chart" role="img" aria-label={`Open risk by platform area. ${description}`}>
        <ResponsiveContainer width="100%" height={Math.max(chartData.length * 44 + 60, 220)}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="var(--border)" horizontal={false} />
            <XAxis
              type="number"
              allowDecimals={false}
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <YAxis
              type="category"
              dataKey="area"
              width={160}
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            />
            <Legend />
            {payload.tier_order.map((tier) => (
              <Bar key={tier} dataKey={tier} stackId="tier" name={payload.tiers[tier].label} fill={TIER_COLORS[tier]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {hidden.length > 0 && (
        <p className="risk-register__chart-note">
          And {hidden.length} more area{hidden.length === 1 ? '' : 's'} holding {hiddenTotal} open item
          {hiddenTotal === 1 ? '' : 's'} between them.
        </p>
      )}

      <p className="risk-register__chart-note">
        Areas are ordered by severity, counting a critical item ahead of a high one and a high one ahead of a moderate
        one, then by how many open items they hold.
      </p>
    </div>
  );
};
